/**
 * Password Validation Utility
 *
 * Enforces strong password requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

const MIN_LENGTH = 12;
const UPPERCASE_REGEX = /[A-Z]/;
const LOWERCASE_REGEX = /[a-z]/;
const NUMBER_REGEX = /[0-9]/;
const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

/**
 * Validate password strength
 * @param password - The password to validate
 * @returns Validation result with isValid flag and error messages
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];

  if (!password) {
    return {
      isValid: false,
      errors: ['Password is required']
    };
  }

  if (password.length < MIN_LENGTH) {
    errors.push(`Password must be at least ${MIN_LENGTH} characters long`);
  }

  if (!UPPERCASE_REGEX.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!LOWERCASE_REGEX.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!NUMBER_REGEX.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (!SPECIAL_CHAR_REGEX.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*...)');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Get password strength indicator
 * @param password - The password to check
 * @returns Strength level: 'weak', 'medium', 'strong'
 */
export function getPasswordStrength(password: string): 'weak' | 'medium' | 'strong' {
  if (!password) return 'weak';

  let score = 0;

  // Length check
  if (password.length >= MIN_LENGTH) score++;
  if (password.length >= 16) score++;

  // Complexity checks
  if (UPPERCASE_REGEX.test(password)) score++;
  if (LOWERCASE_REGEX.test(password)) score++;
  if (NUMBER_REGEX.test(password)) score++;
  if (SPECIAL_CHAR_REGEX.test(password)) score++;

  // Additional complexity
  if (password.length >= 20) score++;

  if (score <= 3) return 'weak';
  if (score <= 5) return 'medium';
  return 'strong';
}

/**
 * Get helper text for password requirements
 * @returns Array of requirement strings
 */
export function getPasswordRequirements(): string[] {
  return [
    `At least ${MIN_LENGTH} characters long`,
    'Contains uppercase letter (A-Z)',
    'Contains lowercase letter (a-z)',
    'Contains number (0-9)',
    'Contains special character (!@#$%...)'
  ];
}

/**
 * Check if password meets minimum requirements
 * @param password - The password to check
 * @returns True if password is valid, false otherwise
 */
export function isPasswordValid(password: string): boolean {
  return validatePassword(password).isValid;
}
